// The slot of a program and everything inside it: the set prescription, the
// week plan, the variants and the group. Split out of program.ts for its size
// (#356); program.ts re-exports it, as it does programTargets and programV3.
import { z } from "zod";
import { slugString } from "./slug.js";
import { SIGNED_WEIGHT_KG_MAX, SIGNED_WEIGHT_KG_MIN } from "../units.js";
import { authoredText } from "../authoredText.js";
import { AmrapStop, SetRepsRange } from "./programV3.js";
import { Side } from "../side.js";

const ExerciseSlug = slugString(64);

// Per-set intensity prescription. Distinct kinds because the resolver
// (Phase 5.3) and the workout logger render each one differently:
//
//   none          : no prescribed intensity. Athlete picks the weight
//                    in-session. Common for warm-ups, mobility holds,
//                    technique-only sets.
//   absolute      : fixed weight in kg. The program author types the
//                    number, resolved as-is at plan-start. Used when
//                    MAX isn't tracked for the lift, or when the
//                    program author overrides on a per-cycle basis.
//   percent-of-max: the program author types a percentage of MAX.
//                    Resolved against the athlete's live MAX at
//                    plan-start, so a MAX update before execution
//                    shifts targets without re-editing the program.
//   history       : explicit "use the athlete's last-logged weight on
//                    this exercise". Equivalent to `none` at the data
//                    layer (no prescribed kg) but distinct intent: the
//                    coach is asking the resolver to look back, not
//                    leaving the cell blank. resolveDefaultWeight reads
//                    this kind and falls through to the history step.
//   rpe           : target RPE 1..10, weight chosen by the athlete to
//                    hit that exertion level. The resolver keeps it as
//                    an RPE target on the planned set.
//   rir           : target RIR 0..10 (reps-in-reserve), weight chosen by
//                    the athlete. Canonical effort kind: RIR is JL's
//                    internal effort scale (see effortScale.ts). The
//                    Logger renders whichever scale the athlete prefers,
//                    converting at the edge. RIR 0 = to failure.
export const SetIntensity = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("none") }),
  z.object({
    kind: z.literal("absolute"),
    weightKg: z.number().min(SIGNED_WEIGHT_KG_MIN).max(SIGNED_WEIGHT_KG_MAX),
  }),
  z.object({
    kind: z.literal("percent-of-max"),
    pct: z.number().min(0).max(200),
  }),
  z.object({ kind: z.literal("history") }),
  z.object({
    kind: z.literal("rpe"),
    target: z.number().min(1).max(10),
  }),
  z.object({
    kind: z.literal("rir"),
    target: z.number().min(0).max(10),
  }),
]);
export type SetIntensity = z.infer<typeof SetIntensity>;

// VARIANT CADENCE: when this variant is the active exercise for the
// slot. A slot with two variants {Leg Press @ every-other phase 0,
// Hack Squat @ every-other phase 1} alternates the two each week.
//
//   every-week : always active (single-variant slot, most common)
//   alternating: active when (week - phaseOffset) % 2 == 0
//   third-week : active when (week - phaseOffset) % 3 == 0
//
// phaseOffset shifts the cycle. Multi-variant slots typically have
// non-zero offsets so the cadence covers every week.
export const VariantCadence = z.enum(["every-week", "alternating", "third-week"]);
export type VariantCadence = z.infer<typeof VariantCadence>;

export const Variant = z.object({
  exerciseId: ExerciseSlug,
  // Snapshot at write time; server stamps from catalog so the program
  // view never depends on the catalog still containing the slug.
  exerciseName: z.string().min(1).max(120).optional(),
  cadence: VariantCadence,
  phaseOffset: z.number().int().min(0).max(3),
});
export type Variant = z.infer<typeof Variant>;

// WORK AXIS: how this set is BOUNDED in time (HI-1, next/interval-
// training.md §9.1). Absent = the reps-bounded set that has always
// existed, so every program written before this delta reads back
// unchanged and no migration exists.
//
//   countdown: a bout of durationSec. The work timer runs it down and
//               stamps the completion at zero, which is the same effect
//               the athlete's tap has today.
//   countup  : an open bout. The athlete's tap closes it (For Time, a
//               free hold), exactly as a reps set closes today.
//
// ⛔ There is deliberately NO third field for "how this set advances":
// the advance mode is DERIVED (countdown → automatic; countup or no
// `work` at all → the existing completion tap). One tap raises one
// prompt, and a stored mode could contradict the bound.
export const SetWork = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("countdown"),
    // Twin of Exercise.defaultDurationSec: same `*Sec` suffix the
    // prescription side uses everywhere (defaultRestSec, defaultDurationSec).
    // The stored RESULT stays LogSet.duration, bare, as it is today.
    durationSec: z.number().int().min(1).max(3600),
  }),
  z.object({ kind: z.literal("countup") }),
]);
export type SetWork = z.infer<typeof SetWork>;

// SET PRESCRIPTION: one planned set inside a single week of a slot.
// reps + intensity carry the prescription. reps == 0 with intensity.kind
// == "none" is the AMRAP / freestyle case (the athlete writes reps in
// session). per-set notes override the slot-wide instructions for that
// one set: pause length, tempo override, "stop two reps short".
export const SetPrescription = z.object({
  reps: z.number().int().min(0).max(50),
  // #336 S3: the range around `reps` (programV3.ts).
  ...SetRepsRange,
  // Per-set AMRAP ("5+"): reps stays the FLOOR the athlete must reach,
  // the flag tells the logger to render "5+ reps". Additive 2026-06-11
  // (composer reps syntax "N+"); the older slot-level amrapLastSet
  // keeps working and the resolver ORs the two.
  amrap: z.boolean().optional(),
  // #355: where the AMRAP stops (programV3.ts).
  amrapStop: AmrapStop.optional(),
  intensity: SetIntensity,
  // Time axis (HI-1). Both optional and both additive: absent `work`
  // means a reps set, absent `restSec` means the rest ladder decides
  // (exercise.defaultRestSec → profile.defaultRestSec), which is what
  // every set does today. A HIIT bout prescribes both: "30s of work,
  // then 90s of rest", eight times.
  work: SetWork.optional(),
  restSec: z.number().int().min(0).max(3600).optional(),
  notes: authoredText(200).optional(),
});
export type SetPrescription = z.infer<typeof SetPrescription>;

// WEEK PLAN: the set list for one week of the cycle. The slot's weeks[]
// array has length equal to Program.cycleWeeks, indexed week-1 = 0.
// Each active week has its own number of sets (#336 S1): a deload round
// can run one set fewer.
//
// sets: [] means the slot is INACTIVE that week: "this exercise only
// runs W1-W3 of a 6-week cycle". Introduced for the scheduler-composer
// editor (P0, next/program-editor-redesign.md §3.1): a block that
// targets a subset of weeks expands to empty WeekPlans on the others.
// The resolver yields no entry for an inactive week, and a (session,
// week) whose slots are all inactive is skipped by next-session math
// (sessionHasEntriesForWeek). Programs written before this delta are
// all min(1)-shaped, so no migration.
/** The most sets one round of a slot can have. */
export const MAX_ROUND_SETS = 20;

export const WeekPlan = z.object({
  sets: z.array(SetPrescription).min(0).max(MAX_ROUND_SETS),
});
export type WeekPlan = z.infer<typeof WeekPlan>;

// Same shape as WorkoutEntryGroup / PlannedEntryGroup: when ≥2 slots
// in the same session carry the same group.id, the resolver tags the
// resulting PlannedExerciseEntries with that group, so the workout
// logger renders them as a superset/circuit window. Singletons are
// dropped at resolution time (matches the rest of the stack).
// GROUP CADENCE: the RHYTHM of the group, as opposed to `work` above,
// which is what one set IS (HI-1, next/interval-training.md §9.2). The
// split is the reason EMOM and AMRAP don't demote the set back to a
// stopwatch: the set stays the thing you do, the group says the pace.
//
//   rounds: repeat the group N times (a circuit, the cycles of a Tabata)
//   amrap : repeat until the cap, counting the rounds you finished
//   emom  : every round is a fixed window
//
// ⚠️ Absent = today's behaviour exactly: rounds are the set count and
// the athlete advances by hand. The first HIIT cut is ONE exercise and
// does not read this: it is stored now, with the time axis above,
// because both move `tooling/api-contract` and one deploy is cheaper
// than two. The UI that authors and runs it lands with the circuit
// slice, and PlannedEntryGroup / WorkoutEntryGroup grow the twin field
// with it, deliberately NOT before, so the three shapes never carry a
// field that only one of them can honour.
export const GroupCadence = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("rounds"),
    count: z.number().int().min(1).max(99),
  }),
  z.object({
    kind: z.literal("amrap"),
    timeCapSec: z.number().int().min(1).max(7200),
  }),
  z.object({
    kind: z.literal("emom"),
    windowSec: z.number().int().min(1).max(3600),
    rounds: z.number().int().min(1).max(99),
  }),
]);
export type GroupCadence = z.infer<typeof GroupCadence>;

export const SlotGroup = z.object({
  id: z.string().min(1).max(64),
  kind: z.enum(["superset", "circuit"]),
  cadence: GroupCadence.optional(),
  // Same optional label WorkoutEntryGroup already carries (workout.ts:267).
  // A program could hold a superset but not its NAME, so the one the picker
  // asks for was typed and then dropped on save: the program editor mounts
  // that same picker (PE-26). Absent = the client renders its default
  // "Superset N" label, so old programs keep the numbering they have.
  name: z.string().trim().min(1).max(60).optional(),
});
export type SlotGroup = z.infer<typeof SlotGroup>;

// SLOT: one planned exercise position in a session. Multi-variant
// slots cycle exercises across weeks. The slot owns the per-week per-set
// matrix and the slot-wide AMRAP / 1MAX flags.
//
// `instructions` is the slot-wide cue text shown during workout
// execution: tempo, pause length, technique focus. Per-set notes
// override at the individual set level when only one set deviates.
export const Slot = z.object({
  id: z.string().min(1),
  position: z.number().int().min(0).max(50),
  variants: z.array(Variant).min(1).max(4),
  weeks: z.array(WeekPlan).min(1).max(12),
  // AMRAP on the last working set, repeated every session in the cycle.
  // Q6 default at program-create time: AMRAP on the primary lift's
  // session in cycles ≥ 4 weeks (off in deload). User overrides here.
  amrapLastSet: z.boolean().optional(),
  // 1MAX attempt on the test week (last week of cycle, before deload).
  // Q6 default placed by the editor; user override here.
  maxAttempt: z.boolean().optional(),
  instructions: authoredText(500).optional(),
  // Per-slot default for the Logger's RPE column. Propagated onto the
  // resolved PlannedExerciseEntry.rpeEnabled by programResolution.ts so
  // downstream consumers read a single field regardless of whether the
  // session originated from a Plan or a Program slot.
  rpeEnabled: z.boolean().optional(),
  // Per-slot default for the Logger's RIR column. Same propagation as
  // rpeEnabled. Phase 8.
  rirEnabled: z.boolean().optional(),
  // Optional grouping. Forwarded to the resolved
  // PlannedExerciseEntry.group when this slot is active for the
  // resolved week.
  group: SlotGroup.optional(),
  // #356: the model row (ModelRow.id) this slot was written from. Absent on
  // a slot made by hand: «More time» has nothing to add there.
  rowId: slugString(64).optional(),
  // #75: the side the coach prescribes (side.ts), «right side only».
  // Absent: nothing said, as on every slot written before it.
  side: Side.optional(),
});
export type Slot = z.infer<typeof Slot>;
